import { z } from 'zod';
import { FIELD_LIMITS, APPLICATION_PAGE_SIZE_DEFAULT, APPLICATION_PAGE_SIZE_MAX } from './constants.js';
import {
  applicationStatusSchema,
  type ApplicationStatus,
  type EmploymentType,
  type JobStatus,
  type WorkplaceType,
} from './enums.js';
import type { AnswerValue, AnswersOutput } from './answers.js';
import type { QuestionSnapshot } from './question.js';

const optionalText = (max: number) =>
  z
    .string()
    .max(max)
    .nullish()
    .transform((value) => {
      const trimmed = value?.trim();
      return trimmed ? trimmed : null;
    });

const optionalUrl = z
  .string()
  .max(FIELD_LIMITS.url)
  .nullish()
  .transform((value) => value?.trim() || null)
  .refine(
    (value) => value === null || z.url({ protocol: /^https?$/ }).safeParse(value).success,
    'Enter a valid http(s) URL',
  );

/**
 * Candidate-supplied fields that are not the resume file and not custom answers.
 * The resume is validated separately because it arrives as a multipart stream.
 */
export const applicationBaseSchema = z.object({
  firstName: z.string().trim().min(1, 'First name is required').max(FIELD_LIMITS.name),
  lastName: z.string().trim().min(1, 'Last name is required').max(FIELD_LIMITS.name),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Email is required')
    .max(FIELD_LIMITS.email)
    .pipe(z.email('Enter a valid email address')),
  phone: optionalText(FIELD_LIMITS.phone),
  currentLocation: optionalText(FIELD_LIMITS.location),
  linkedinUrl: optionalUrl,
  portfolioUrl: optionalUrl,
  coverLetter: optionalText(FIELD_LIMITS.coverLetter),
  privacyAcknowledged: z
    .boolean()
    .refine((value) => value, 'Please acknowledge the applicant privacy notice'),
});

export type ApplicationBaseInput = z.input<typeof applicationBaseSchema>;
export type ApplicationBaseOutput = z.output<typeof applicationBaseSchema>;

/** A job that requires a cover letter tightens the optional field to required. */
export function applicationBaseSchemaFor(options: { coverLetterRequired: boolean }) {
  if (!options.coverLetterRequired) return applicationBaseSchema;
  return applicationBaseSchema.refine((value) => value.coverLetter !== null, {
    path: ['coverLetter'],
    message: 'A cover letter is required for this role',
  });
}

export const applicationStatusUpdateSchema = z.object({
  status: applicationStatusSchema,
  note: z.string().trim().max(FIELD_LIMITS.noteBody).optional(),
});
export type ApplicationStatusUpdateInput = z.infer<typeof applicationStatusUpdateSchema>;

export const applicationNoteSchema = z.object({
  body: z.string().trim().min(1, 'Note cannot be empty').max(FIELD_LIMITS.noteBody),
});
export type ApplicationNoteInput = z.infer<typeof applicationNoteSchema>;

export const APPLICATION_SORT_FIELDS = ['submittedAt', 'lastName', 'status'] as const;
export const applicationSortFieldSchema = z.enum(APPLICATION_SORT_FIELDS);
export type ApplicationSortField = z.infer<typeof applicationSortFieldSchema>;

export const adminApplicationListQuerySchema = z.object({
  q: z.string().trim().max(160).optional(),
  jobId: z.uuid().optional(),
  status: applicationStatusSchema.optional(),
  submittedFrom: z.iso.date().optional(),
  submittedTo: z.iso.date().optional(),
  sort: applicationSortFieldSchema.default('submittedAt'),
  order: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce
    .number()
    .int()
    .min(1)
    .max(APPLICATION_PAGE_SIZE_MAX)
    .default(APPLICATION_PAGE_SIZE_DEFAULT),
});
export type AdminApplicationListQuery = z.infer<typeof adminApplicationListQuerySchema>;

export interface ApplicationAnswerDto {
  questionId: string | null;
  label: string;
  type: QuestionSnapshot['type'];
  required: boolean;
  choices: string[];
  value: AnswerValue;
  sortOrder: number;
}

export interface AdminApplicationSummary {
  id: string;
  reference: string;
  firstName: string;
  lastName: string;
  email: string;
  status: ApplicationStatus;
  submittedAt: string;
  job: { id: string; title: string; slug: string };
}

export interface ApplicationStatusEventDto {
  id: string;
  fromStatus: ApplicationStatus | null;
  toStatus: ApplicationStatus;
  createdAt: string;
  actor: { id: string; name: string; email: string } | null;
}

export interface ApplicationNoteDto {
  id: string;
  body: string;
  createdAt: string;
  author: { id: string; name: string; email: string } | null;
}

export interface AdminApplicationDetail extends AdminApplicationSummary {
  phone: string | null;
  currentLocation: string | null;
  linkedinUrl: string | null;
  portfolioUrl: string | null;
  coverLetter: string | null;
  resume: { filename: string; contentType: string; size: number } | null;
  answers: ApplicationAnswerDto[];
  statusEvents: ApplicationStatusEventDto[];
  notes: ApplicationNoteDto[];
  updatedAt: string;
}

/**
 * One candidate as a flat spreadsheet row: every column the database holds that
 * is useful to a reviewer, plus a few derived from status history and notes.
 * Long free text is trimmed to a preview; the detail page has the full copy.
 */
export interface AdminApplicationGridRow {
  id: string;
  reference: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  currentLocation: string | null;
  linkedinUrl: string | null;
  portfolioUrl: string | null;
  coverLetterPreview: string | null;
  status: ApplicationStatus;
  submittedAt: string;
  updatedAt: string;
  resume: { filename: string; size: number };
  confirmationEmail: 'SENT' | 'FAILED' | 'PENDING';
  job: {
    id: string;
    title: string;
    slug: string;
    department: string;
    locationText: string;
    workplaceType: WorkplaceType;
    employmentType: EmploymentType;
    status: JobStatus;
  };
  /** When the current status was set; the submission time if it never changed. */
  statusChangedAt: string;
  /** Null for the initial status, or once the admin who set it is removed. */
  statusChangedBy: string | null;
  noteCount: number;
  latestNote: { preview: string; createdAt: string; author: string | null } | null;
  /** Formatted answers keyed by question label, the same flattening the CSV export uses. */
  answers: Record<string, string>;
}

export interface AdminApplicationGrid {
  rows: AdminApplicationGridRow[];
  /** Every distinct question label across the rows, sorted. */
  answerLabels: string[];
  /** All applications in the database, which exceeds `rows.length` only when truncated. */
  total: number;
  truncated: boolean;
}

/** The only thing a candidate gets back after submitting. */
export interface ApplicationReceipt {
  reference: string;
  submittedAt: string;
  jobTitle: string;
}

export type { AnswersOutput };
